import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react-native';
import { useTheme, SPACING, CONTENT_MAX_WIDTH, TYPOGRAPHY } from '../../theme';
import { useDensity } from '../../hooks/useDensity';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { Skeleton } from './Skeleton';

export interface TableSort {
  key: string;
  direction: 'asc' | 'desc';
}

export interface TableColumn<T> {
  key: string;
  header: string;
  /** Fixed width; omit for flex: 1. */
  width?: number;
  align?: 'left' | 'right';
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  /**
   * Role when the table collapses to cards below `collapseBelow`:
   * 'title' → card heading, 'subtitle' → secondary line,
   * 'detail' → label/value row, 'hidden' → dropped.
   */
  collapse?: 'title' | 'subtitle' | 'detail' | 'hidden';
  /**
   * This cell renders its own controls (a Button, a menu). Such cells sit
   * OUTSIDE the row's press target: with `onRowPress` set the row is a
   * `<button>` on web, and a control nested inside it is invalid HTML that
   * React warns about and that swallows the inner control's own keyboard
   * activation. Interactive columns render after the pressable ones, so
   * declare them last.
   */
  interactive?: boolean;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  /** Controlled sort — sorting the data is the caller's job. */
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  onRowPress?: (row: T) => void;
  /** Below this window width the table renders as a card stack. */
  collapseBelow?: number;
  emptyState?: React.ReactNode;
  loading?: boolean;
  testID?: string;
}

/**
 * Token-compliant data table for desktop information density: labelSmall
 * uppercase header row with sortable-column affordances, hairline row
 * separators, row hover + pointer when pressable. Below `collapseBelow`
 * (default 768) the same columns render as a stack of hairline-divided rows,
 * so call sites never fork on breakpoint. Everything sits on the canvas: no
 * container border, no tinted header, no per-row box.
 */
export function Table<T>({
  columns,
  data,
  keyExtractor,
  sort,
  onSortChange,
  onRowPress,
  collapseBelow = CONTENT_MAX_WIDTH,
  emptyState,
  loading = false,
  testID,
}: TableProps<T>) {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const density = useDensity();
  const collapsed = width < collapseBelow;

  if (loading) {
    return (
      <View testID={testID ? `${testID}-loading` : undefined} style={styles.loadingStack}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} width="100%" height={collapsed ? 88 : 44} />
        ))}
      </View>
    );
  }

  if (data.length === 0) {
    return <>{emptyState ?? null}</>;
  }

  if (collapsed) {
    return (
      <View style={styles.cardStack} testID={testID}>
        {data.map((row) => (
          <CollapsedRowCard
            key={keyExtractor(row)}
            row={row}
            columns={columns}
            onPress={onRowPress ? () => onRowPress(row) : undefined}
          />
        ))}
      </View>
    );
  }

  const cellPad = density === 'compact' ? SPACING.sm : SPACING.smd;

  return (
    <View style={styles.table} testID={testID}>
      <View style={[styles.headerRow, { borderBottomColor: colors.border }]}>
        {columns.map((col) => (
          <HeaderCell
            key={col.key}
            column={col}
            sort={sort}
            onSortChange={onSortChange}
            cellPad={cellPad}
          />
        ))}
      </View>
      {data.map((row, i) => (
        <TableRow
          key={keyExtractor(row)}
          row={row}
          columns={columns}
          onPress={onRowPress ? () => onRowPress(row) : undefined}
          isLast={i === data.length - 1}
          cellPad={cellPad}
        />
      ))}
    </View>
  );
}

function HeaderCell<T>({
  column,
  sort,
  onSortChange,
  cellPad,
}: {
  column: TableColumn<T>;
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  cellPad: number;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover(Boolean(column.sortable && onSortChange));
  const isActive = sort?.key === column.key;
  const sortable = Boolean(column.sortable && onSortChange);

  const handlePress = () => {
    if (!sortable || !onSortChange) return;
    onSortChange({
      key: column.key,
      direction: isActive && sort?.direction === 'desc' ? 'asc' : 'desc',
    });
  };

  const SortIcon = !isActive ? ArrowUpDown : sort?.direction === 'asc' ? ArrowUp : ArrowDown;

  const content = (
    <>
      <Text
        style={[
          styles.headerText,
          { color: isActive ? colors.text : colors.textSecondary },
          column.align === 'right' && styles.alignRight,
        ]}
        numberOfLines={1}>
        {column.header}
      </Text>
      {sortable && <SortIcon size={12} color={isActive ? colors.primary : colors.textTertiary} />}
    </>
  );

  const cellStyle = [
    styles.cell,
    styles.headerCell,
    { paddingVertical: cellPad },
    column.width != null ? { width: column.width } : styles.flexCell,
    column.align === 'right' && styles.cellRight,
  ];

  if (!sortable) {
    return <View style={cellStyle}>{content}</View>;
  }

  return (
    <TouchableOpacity
      onPress={handlePress}
      {...hoverProps}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={
        isActive
          ? `Sort by ${column.header}, sorted ${sort?.direction === 'asc' ? 'ascending' : 'descending'}`
          : `Sort by ${column.header}`
      }
      accessibilityState={{ selected: isActive }}
      // aria-sort belongs on a columnheader, and RN-web renders this as a
      // <button role="button">, so axe rejected it (aria-allowed-attr,
      // critical, once per sortable column). There is no real table in the
      // DOM to host it — it is all divs — so carry the state as aria-pressed
      // and put the direction in the label, which is what a screen reader
      // reads out anyway. RN-web drops accessibilityState, hence the raw
      // aria-* prop (see OptionsPanel).
      aria-pressed={isActive}
      style={[cellStyle, hovered && { backgroundColor: colors.hoverSurface }, CURSOR_POINTER]}>
      {content}
    </TouchableOpacity>
  );
}

function TableRow<T>({
  row,
  columns,
  onPress,
  isLast,
  cellPad,
}: {
  row: T;
  columns: TableColumn<T>[];
  onPress?: () => void;
  isLast: boolean;
  cellPad: number;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover(Boolean(onPress));

  const renderCell = (col: TableColumn<T>) => (
    <View
      key={col.key}
      style={[
        styles.cell,
        { paddingVertical: cellPad },
        col.width != null ? { width: col.width } : styles.flexCell,
        col.align === 'right' && styles.cellRight,
      ]}>
      {col.render ? (
        col.render(row)
      ) : (
        <Text style={[styles.cellText, { color: colors.text }]} numberOfLines={1}>
          {String((row as Record<string, unknown>)[col.key] ?? '')}
        </Text>
      )}
    </View>
  );

  const rowStyle = [
    styles.row,
    !isLast && { borderBottomColor: colors.divider, borderBottomWidth: StyleSheet.hairlineWidth },
    hovered && { backgroundColor: colors.hoverSurface },
  ];

  if (!onPress) {
    return <View style={rowStyle}>{columns.map(renderCell)}</View>;
  }

  // The press target covers the plain cells only; cells that carry their own
  // controls are siblings of it, never children (see TableColumn.interactive).
  return (
    <View style={[rowStyle, CURSOR_POINTER]} {...hoverProps}>
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        accessibilityRole="button"
        style={styles.rowPressArea}>
        {columns.filter((col) => !col.interactive).map(renderCell)}
      </TouchableOpacity>
      {columns.filter((col) => col.interactive).map(renderCell)}
    </View>
  );
}

function CollapsedRowCard<T>({
  row,
  columns,
  onPress,
}: {
  row: T;
  columns: TableColumn<T>[];
  onPress?: () => void;
}) {
  const { colors } = useTheme();
  const cellValue = (col: TableColumn<T>) =>
    col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '');

  const title = columns.find((c) => c.collapse === 'title');
  const subtitle = columns.find((c) => c.collapse === 'subtitle');
  const details = columns.filter(
    (c) => (c.collapse ?? 'detail') === 'detail' && c !== title && !c.interactive
  );
  // Same rule as the desktop row: controls never nest inside the card's own
  // press target. They get their own strip under the details instead.
  const actions = columns.filter((c) => c.interactive && c.collapse !== 'hidden');

  const body = (
    <>
      {title && (
        <Text style={[styles.cardTitle, { color: colors.text }]} numberOfLines={1}>
          {cellValue(title)}
        </Text>
      )}
      {subtitle && (
        <Text style={[styles.cardSubtitle, { color: colors.textSecondary }]} numberOfLines={1}>
          {cellValue(subtitle)}
        </Text>
      )}
      <View style={styles.cardDetails}>
        {details.map((col) => (
          <View key={col.key} style={styles.cardDetailRow}>
            {/* The column name, so it is content, not a hint. textTertiary is
                sub-AA by design and fails contrast at this size. */}
            <Text style={[styles.cardDetailLabel, { color: colors.textSecondary }]}>
              {col.header}
            </Text>
            <View style={styles.cardDetailValue}>
              {typeof cellValue(col) === 'string' ? (
                <Text style={[styles.cellText, { color: colors.text }]}>{cellValue(col)}</Text>
              ) : (
                cellValue(col)
              )}
            </View>
          </View>
        ))}
      </View>
    </>
  );

  return (
    <View style={[styles.collapsedRow, { borderBottomColor: colors.divider }]}>
      {onPress ? (
        <TouchableOpacity onPress={onPress} activeOpacity={0.7} accessibilityRole="button">
          {body}
        </TouchableOpacity>
      ) : (
        body
      )}
      {actions.length > 0 && (
        <View style={styles.cardActions}>
          {actions.map((col) => (
            <View key={col.key}>{cellValue(col)}</View>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  // Hairline rows on the canvas, not a filled bordered card with a tinted
  // header. The table only renders when there are rows, and dev had no agent
  // clients until July 2026, so this primitive never appeared on screen and
  // the Design B sweep never reached it.
  table: {},
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  headerText: {
    ...TYPOGRAPHY.labelSmall,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowPressArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  cell: {
    paddingHorizontal: SPACING.smd,
    justifyContent: 'center',
  },
  flexCell: {
    flex: 1,
  },
  cellRight: {
    alignItems: 'flex-end',
  },
  alignRight: {
    textAlign: 'right',
  },
  cellText: {
    ...TYPOGRAPHY.body,
  },
  loadingStack: {
    gap: SPACING.sm,
  },
  // No gap: collapsed rows are separated by their own hairline, the way the
  // claimed-property rows on my-properties are.
  cardStack: {},
  collapsedRow: {
    paddingVertical: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  cardTitle: {
    ...TYPOGRAPHY.button,
  },
  cardSubtitle: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  cardDetails: {
    marginTop: SPACING.sm,
    gap: SPACING.xs,
  },
  cardActions: {
    marginTop: SPACING.smd,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  cardDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACING.sm,
  },
  cardDetailLabel: {
    ...TYPOGRAPHY.labelSmall,
  },
  cardDetailValue: {
    flexShrink: 1,
    alignItems: 'flex-end',
  },
});
